/**
 * FRMWRK ambient product color system.
 *
 * Collection cards derive a soft ambient gradient from the hovered product.
 * Editorial product pages keep the fixed gradient chosen in the theme editor.
 */
(() => {
  const html = document.documentElement;
  const body = document.body;

  if (!body?.classList.contains('frmwrk-theme')) return;
  if (!body.classList.contains('index')) return;

  const productSelector = '.product-item';
  const imageSelector = '.product-image, img';
  const sampleCache = new Map();
  const boundCards = new WeakSet();

  const readPalette = () => {
    const styles = getComputedStyle(html);

    const pair = name => [
      styles
        .getPropertyValue(`--frmwrk-${name}-a`)
        .trim(),
      styles
        .getPropertyValue(`--frmwrk-${name}-b`)
        .trim()
    ];

    return [
      pair('chalk'),
      pair('stone'),
      pair('olive'),
      pair('pepper'),
      pair('ink')
    ];
  };

  const palette = readPalette();

  const initialPair = [
    getComputedStyle(html)
      .getPropertyValue('--frmwrk-active-a')
      .trim() || palette[0][0],
    getComputedStyle(html)
      .getPropertyValue('--frmwrk-active-b')
      .trim() || palette[0][1]
  ];

  let basePair = initialPair;
  let activationToken = 0;
  let hoverIntentTimer = 0;

  const clamp = (value, min, max) =>
    Math.min(max, Math.max(min, value));

  const toHex = value =>
    Math.round(clamp(value, 0, 255))
      .toString(16)
      .padStart(2, '0');

  const rgbToHex = rgb =>
    `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;

  const shade = (rgb, amount) =>
    rgb.map(channel =>
      amount >= 0
        ? channel + (255 - channel) * amount
        : channel * (1 + amount)
    );

  const hash = text => {
    let value = 0;

    for (
      let index = 0;
      index < text.length;
      index += 1
    ) {
      value =
        ((value << 5) -
          value +
          text.charCodeAt(index)) |
        0;
    }

    return Math.abs(value);
  };

  const fallbackPair = card => {
    const text = card.textContent
      .trim()
      .toLowerCase();

    const keywordMap = [
      [
        ['chalk', 'white', 'cream', 'bone', 'ivory'],
        0
      ],
      [
        ['stone', 'taupe', 'beige', 'sand', 'grey', 'gray'],
        1
      ],
      [
        ['olive', 'green', 'khaki', 'moss'],
        2
      ],
      [
        ['pepper', 'charcoal', 'black'],
        3
      ],
      [
        ['ink', 'navy', 'blue'],
        4
      ]
    ];

    const match = keywordMap.find(([words]) =>
      words.some(word => text.includes(word))
    );

    return palette[
      match
        ? match[1]
        : hash(text) % palette.length
    ];
  };

  const setAmbient = pair => {
    if (!pair?.[0] || !pair?.[1]) return;

    html.style.setProperty(
      '--frmwrk-active-a',
      pair[0]
    );

    html.style.setProperty(
      '--frmwrk-active-b',
      pair[1]
    );
  };

  const loadBitmap = async url => {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error('Image request failed');
    }

    const blob = await response.blob();

    if ('createImageBitmap' in window) {
      return createImageBitmap(blob);
    }

    return new Promise((resolve, reject) => {
      const objectUrl =
        URL.createObjectURL(blob);

      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(
          new Error('Image decode failed')
        );
      };

      image.src = objectUrl;
    });
  };

  const sampleImage = async image => {
    const url =
      image?.currentSrc || image?.src;

    if (!url) {
      throw new Error('No product image');
    }

    if (sampleCache.has(url)) {
      return sampleCache.get(url);
    }

    const promise = (async () => {
      const bitmap = await loadBitmap(url);
      const canvas =
        document.createElement('canvas');

      const size = 28;

      canvas.width = size;
      canvas.height = size;

      const context = canvas.getContext(
        '2d',
        {
          willReadFrequently: true
        }
      );

      context.drawImage(
        bitmap,
        0,
        0,
        size,
        size
      );

      bitmap.close?.();

      const pixels = context.getImageData(
        0,
        0,
        size,
        size
      ).data;

      let red = 0;
      let green = 0;
      let blue = 0;
      let totalWeight = 0;

      for (
        let index = 0;
        index < pixels.length;
        index += 4
      ) {
        const alpha =
          pixels[index + 3] / 255;

        if (alpha < 0.45) continue;

        const pixel = [
          pixels[index],
          pixels[index + 1],
          pixels[index + 2]
        ];

        const luminance =
          pixel[0] * 0.2126 +
          pixel[1] * 0.7152 +
          pixel[2] * 0.0722;

        const spread =
          Math.max(...pixel) -
          Math.min(...pixel);

        if (
          luminance > 246 &&
          spread < 8
        ) {
          continue;
        }

        const weight =
          alpha * (1 + spread / 255);

        red += pixel[0] * weight;
        green += pixel[1] * weight;
        blue += pixel[2] * weight;
        totalWeight += weight;
      }

      if (totalWeight < 3) {
        throw new Error(
          'No representative color'
        );
      }

      const average = [
        red / totalWeight,
        green / totalWeight,
        blue / totalWeight
      ];

      return [
        rgbToHex(shade(average, 0.45)),
        rgbToHex(shade(average, 0.12))
      ];
    })();

    sampleCache.set(url, promise);

    return promise;
  };

  const activateCard = async card => {
    const token = ++activationToken;

    document
      .querySelectorAll(
        `${productSelector}.frmwrk-product-active`
      )
      .forEach(item => {
        if (item !== card) {
          item.classList.remove(
            'frmwrk-product-active'
          );
        }
      });

    card.classList.add(
      'frmwrk-product-active'
    );

    body.classList.add(
      'frmwrk-ambient-active'
    );

    try {
      const image = card.querySelector(imageSelector);
      const sampledPair = await sampleImage(image);

      if (
        token === activationToken &&
        card.classList.contains('frmwrk-product-active')
      ) {
        setAmbient(sampledPair);
      }
    } catch (_error) {
      if (
        token === activationToken &&
        card.classList.contains('frmwrk-product-active')
      ) {
        setAmbient(fallbackPair(card));
      }
    }
  };

  const restoreBase = card => {
    activationToken += 1;

    card.classList.remove(
      'frmwrk-product-active'
    );

    body.classList.remove(
      'frmwrk-ambient-active'
    );

    setAmbient(basePair);
  };

  const queueActivation = card => {
    window.clearTimeout(hoverIntentTimer);

    hoverIntentTimer = window.setTimeout(
      () => activateCard(card),
      90
    );
  };

  const queueRestore = card => {
    window.clearTimeout(hoverIntentTimer);

    hoverIntentTimer = window.setTimeout(
      () => restoreBase(card),
      150
    );
  };

  const bindCard = card => {
    if (boundCards.has(card)) return;

    boundCards.add(card);

    const image = card.querySelector(imageSelector);

    if (image) {
      const preloadColour = () => {
        sampleImage(image).catch(() => {
          /* The name-based palette remains available as fallback. */
        });
      };

      if (image.complete) {
        preloadColour();
      } else {
        image.addEventListener('load', preloadColour, {
          once: true
        });
      }
    }

    card.addEventListener(
      'pointerenter',
      () => queueActivation(card)
    );

    card.addEventListener(
      'pointerleave',
      () => queueRestore(card)
    );

    card.addEventListener(
      'focusin',
      () => activateCard(card)
    );

    card.addEventListener(
      'focusout',
      event => {
        if (
          !card.contains(event.relatedTarget)
        ) {
          restoreBase(card);
        }
      }
    );
  };

  const bindProductPage = async () => {
    const product =
      document.querySelector(
        '.main-product'
      );

    if (!product) return;
    if (product.closest('.frmwrk-product-editorial')) return;

    const visibleImage = () =>
      [
        ...product.querySelectorAll(
          '.product-detail__media img'
        )
      ].find(
        image =>
          image.offsetParent !== null
      ) ||
      product.querySelector(
        '.product-detail__media img'
      );

    const updateBase = async () => {
      const image = visibleImage();
      const fallback =
        fallbackPair(product);

      basePair = fallback;
      setAmbient(basePair);

      try {
        basePair =
          await sampleImage(image);

        if (
          !body.classList.contains(
            'frmwrk-ambient-active'
          )
        ) {
          setAmbient(basePair);
        }
      } catch (_error) {
        // Keep the curated fallback palette.
      }
    };

    await updateBase();

    product.addEventListener(
      'change',
      () =>
        window.setTimeout(
          updateBase,
          80
        )
    );

    product.addEventListener(
      'variant:change',
      updateBase
    );
  };

  const scan = root => {
    if (
      root.matches?.(productSelector)
    ) {
      bindCard(root);
    }

    root
      .querySelectorAll?.(
        productSelector
      )
      .forEach(bindCard);
  };

  scan(document);
  bindProductPage();

  const observer =
    new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(
          node => {
            if (
              node.nodeType ===
              Node.ELEMENT_NODE
            ) {
              scan(node);
            }
          }
        );
      });
    });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

if (!customElements.get('frmwrk-card-quick-add')) {
  customElements.define(
    'frmwrk-card-quick-add',
    class FrmwrkCardQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.trigger = this.querySelector(
          '[data-frmwrk-quick-trigger]'
        );
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector(
          'input[name="id"]'
        );
        this.submitButton = this.querySelector(
          '[data-frmwrk-quick-submit]'
        );
        this.closeTimer = null;

        this.trigger?.addEventListener(
          'click',
          event => {
            event.preventDefault();
            event.stopPropagation();
            this.toggle();
          }
        );

        this.addEventListener(
          'pointerenter',
          () => {
            window.clearTimeout(this.closeTimer);
            this.open();
          }
        );

        this.addEventListener(
          'pointerleave',
          () => this.scheduleClose()
        );

        this.addEventListener(
          'click',
          event => {
            const sizeButton = event.target.closest(
              '[data-frmwrk-variant]'
            );

            if (!sizeButton || sizeButton.disabled) return;

            event.preventDefault();
            event.stopPropagation();

            this.variantInput.value =
              sizeButton.dataset.frmwrkVariant;

            this.querySelectorAll(
              '[data-frmwrk-variant]'
            ).forEach(button => {
              button.classList.toggle(
                'is-selected',
                button === sizeButton
              );
            });

            this.classList.add('is-loading');
            this.form.requestSubmit(this.submitButton);

            window.setTimeout(() => {
              this.classList.remove('is-loading');
              this.close();
            }, 900);
          }
        );

        this.addEventListener(
          'focusout',
          event => {
            if (!this.contains(event.relatedTarget)) {
              this.scheduleClose();
            }
          }
        );
      }

      open() {
        window.clearTimeout(this.closeTimer);
        this.classList.add('is-open');
        this.trigger?.setAttribute(
          'aria-expanded',
          'true'
        );
      }

      toggle() {
        const open =
          !this.classList.contains('is-open');

        if (open) {
          this.open();
        } else {
          this.close();
        }
      }

      scheduleClose() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = window.setTimeout(
          () => this.close(),
          240
        );
      }

      close() {
        window.clearTimeout(this.closeTimer);
        this.classList.remove('is-open');
        this.trigger?.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    }
  );
}
